import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Globe, 
  Shield, 
  Zap, 
  Clock,
  ChevronRight,
  Send,
  Building2,
  Wallet,
  BadgeCheck
} from 'lucide-react';

export default function LandingPage() {
  const { user } = useAuth();

  const services = [
    { name: 'SWIFT', time: '1-3 days', icon: <Building2 className="w-6 h-6" /> },
    { name: 'MoneyGram', time: 'Minutes', icon: <Zap className="w-6 h-6" /> },
    { name: 'Western Union', time: 'Same day', icon: <Globe className="w-6 h-6" /> },
    { name: 'Wire Transfer', time: '1-2 days', icon: <Send className="w-6 h-6" /> },
    { name: 'ACH', time: '1-3 days', icon: <Wallet className="w-6 h-6" /> },
    { name: 'SEPA', time: 'Same day', icon: <BadgeCheck className="w-6 h-6" /> },
  ];

  const features = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: 'Global Coverage',
      description: 'Send money to 200+ countries with multiple transfer options including SWIFT, MoneyGram, and Western Union.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Bank-Grade Security',
      description: 'Enterprise-level encryption and compliance with international financial regulations.'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Real-Time Tracking',
      description: 'Track your transfers in real-time with detailed status updates at every stage.'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Fast Delivery',
      description: 'From instant transfers to scheduled deliveries, choose the speed that fits your needs.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900/20 via-green-900/10 to-teal-900/20">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-sm border-b border-emerald-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <Send className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-emerald-100 font-manrope">MoneyMove</span>
          </Link>
          
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold hover:from-emerald-600 hover:to-teal-700 shadow-lg" data-testid="go-dashboard-btn">
                  Dashboard
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold hover:from-emerald-600 hover:to-teal-700 shadow-lg" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center pt-20 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-emerald-500/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px]" />
        
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Global Money Transfer Platform
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold text-emerald-50 leading-tight mb-6 font-manrope">
              Move Money<br />
              <span className="text-emerald-400">Worldwide</span>
            </h1>
            <p className="text-xl text-emerald-300/70 mb-8 leading-relaxed max-w-lg">
              Send money globally with SWIFT, MoneyGram, Western Union, and more. Real-time tracking, competitive rates, and instant notifications.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-12">
              <Link to={user ? "/dashboard" : "/login"}>
                <Button className="h-14 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-lg rounded-lg hover:from-emerald-600 hover:to-teal-700 shadow-lg" data-testid="hero-cta-btn">
                  Start Sending
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/track">
                <Button variant="outline" className="h-14 px-8 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 rounded-lg text-lg" data-testid="hero-track-btn">
                  Track Transfer
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="flex gap-8">
              <div>
                <p className="text-3xl font-bold text-emerald-50">200+</p>
                <p className="text-emerald-400/60">Countries</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-50">6+</p>
                <p className="text-emerald-400/60">Transfer Methods</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-50">24/7</p>
                <p className="text-emerald-400/60">Support</p>
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-2 gap-4">
              {services.map((service, idx) => (
                <div 
                  key={service.name}
                  className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-5 animate-fade-in shadow-lg"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-3 shadow-md">
                    {service.icon}
                  </div>
                  <h3 className="text-emerald-50 font-semibold mb-1">{service.name}</h3>
                  <p className="text-emerald-400/60 text-sm">{service.time}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-emerald-50 mb-4 font-manrope">
              Why Choose <span className="text-emerald-400">MoneyMove</span>?
            </h2>
            <p className="text-emerald-300/70 text-lg max-w-2xl mx-auto">
              Experience the most comprehensive global money transfer platform with real-time tracking and competitive rates.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-900/40 backdrop-blur-sm border border-emerald-500/20 rounded-xl p-6 hover:border-emerald-500/40 transition-colors shadow-lg"
              >
                <div className="w-14 h-14 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-4 shadow-md">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-emerald-50 mb-2">{feature.title}</h3>
                <p className="text-emerald-400/60">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-emerald-50 mb-6 font-manrope">
            Ready to Move Money Globally?
          </h2>
          <p className="text-xl text-emerald-300/70 mb-8">
            Join thousands of users who trust MoneyMove for their international transfers.
          </p>
          <Link to={user ? "/dashboard" : "/login"}>
            <Button className="h-14 px-10 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-lg rounded-lg hover:from-emerald-600 hover:to-teal-700 shadow-lg" data-testid="cta-register-btn">
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-emerald-500/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                <Send className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold text-emerald-100 font-manrope">MoneyMove</span>
            </div>
            <p className="text-emerald-400/60 text-sm">
              © 2025 MoneyMove. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
